/**
 * The "Source document" preview for a staged row (GDN-3874).
 *
 * The preview endpoint returns the object decoded as UTF-8 with
 * `errors="replace"`, so a binary file arrives with its bytes destroyed. This
 * modal therefore renders by KIND, never by assuming the body is text: a
 * scanned PDF and a JPEG chart are drawn from the object itself via a signed
 * URL, and anything the viewer cannot draw says so instead of printing the
 * decoded bytes.
 */
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

import {
  documentKind,
  documentTypeLabel,
  formatDocumentSize,
  isSafeDocumentUrl,
} from '../utils/document-preview';

/** The preview endpoint's response for one staged row's source document. */
export interface SourceDocumentContent {
  key: string;
  content_type: string;
  size: number;
  /** Absent for binary content; those are fetched through the document URL. */
  text?: string | null;
  truncated: boolean;
}

export interface SourceDocumentModalProps {
  open: boolean;
  onClose: () => void;
  /** The decoded preview body, once loaded. */
  content?: SourceDocumentContent;
  loading: boolean;
  /** The preview request failed, or the row has no source file. */
  error: boolean;
  /** Presigned GET for the object itself, for kinds the body cannot represent. */
  url?: string;
  urlLoading: boolean;
  urlError: boolean;
}

export function SourceDocumentModal({
  open,
  onClose,
  content,
  loading,
  error,
  url,
  urlLoading,
  urlError,
}: SourceDocumentModalProps) {
  const kind = content ? documentKind(content.content_type, content.text ?? undefined) : 'text';
  const safeUrl = isSafeDocumentUrl(url) ? url : undefined;
  // The URL query has not started on the first render after the dialog opens,
  // so it reports neither loading nor error. Treating that as "cannot display"
  // flashes a refusal a frame before the document appears.
  const awaitingUrl = kind !== 'text' && !safeUrl && !urlError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Source document</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <DialogContentText color="error">
            Couldn&apos;t load the document — the row may have no source file.
          </DialogContentText>
        ) : kind === 'text' ? (
          <>
            {content?.truncated && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Showing the first 1 MB of {content.size.toLocaleString()} bytes.
              </Alert>
            )}
            <Box
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 14, m: 0 }}
            >
              {content?.text}
            </Box>
          </>
        ) : urlLoading || awaitingUrl ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : safeUrl && kind === 'pdf' ? (
          <Box
            component="iframe"
            src={safeUrl}
            referrerPolicy="no-referrer"
            title="Source document"
            sx={{ width: '100%', height: '70vh', border: 0 }}
          />
        ) : safeUrl && kind === 'image' ? (
          <Box
            component="img"
            src={safeUrl}
            referrerPolicy="no-referrer"
            alt="Source document"
            sx={{ maxWidth: '100%', maxHeight: '70vh', display: 'block', mx: 'auto' }}
          />
        ) : (
          // Everything the viewer cannot draw. Never the decoded body: for a
          // binary file that is the byte soup this exists to stop.
          <Alert severity="info">
            This source is {documentTypeLabel(content?.content_type)}
            {content?.size ? ` (${formatDocumentSize(content.size)})` : ''}, which this preview
            cannot display.{' '}
            {safeUrl
              ? 'Open it in a new tab to read it.'
              : 'A link to the original file is not available here yet.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {/* The original object, for anything the viewer draws poorly or not at
            all. Offered only once the signed URL is in hand. */}
        {safeUrl && (
          <Button
            component="a"
            href={safeUrl}
            target="_blank"
            rel="noopener noreferrer"
            // A real link, so it announces itself, can be middle-clicked, and
            // survives a blocked popup.
          >
            Open in new tab
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
